/**
 * Mock Factory Service Data
 *
 * Provides mock data for factory service testing and development without backend.
 *
 * IMPORTANT: Uses immutable patterns to prevent test isolation issues.
 * Call resetFactoryMocks() in beforeEach() to ensure clean state between tests.
 */

import type {
  Factory,
  FactoryCreateForm,
  FactoryUpdateForm,
  FactoryFilter,
  FactoryListResponse,
} from '@/schemas/factory/factory.schemas';

/**
 * Factory function to create initial mock factory data
 * Returns a fresh copy to prevent mutation between tests
 */
function createInitialFactories(): Factory[] {
  return [
    {
      id: 'factory-001',
      name: 'Main Production Facility',
      tenantId: 'tenant-123',
      location: {
        address: '123 Industrial Ave',
        city: 'Porto',
        country: 'Portugal',
        postalCode: '4200-001',
        coordinates: [-8.6291, 41.1579],
      },
      settings: {
        timezone: 'Europe/Lisbon',
        workingHours: { start: '08:00', end: '18:00' },
        alertsEnabled: true,
        positionUpdateInterval: 1000,
        maxAssets: 500,
      },
      isActive: true,
      createdAt: new Date('2024-01-10T09:00:00Z'),
      updatedAt: new Date('2024-01-10T09:00:00Z'),
    },
    {
      id: 'factory-002',
      name: 'North Distribution Center',
      tenantId: 'tenant-123',
      location: {
        address: '456 Logistics Blvd',
        city: 'Lisbon',
        country: 'Portugal',
        postalCode: '1000-001',
        coordinates: [-9.1393, 38.7223],
      },
      settings: {
        timezone: 'Europe/Lisbon',
        alertsEnabled: true,
        positionUpdateInterval: 2000,
        maxAssets: 300,
      },
      isActive: true,
      createdAt: new Date('2024-01-15T10:00:00Z'),
      updatedAt: new Date('2024-01-15T10:00:00Z'),
    },
    {
      id: 'factory-003',
      name: 'South Assembly Plant',
      tenantId: 'tenant-123',
      location: {
        address: '789 Manufacturing St',
        city: 'Faro',
        country: 'Portugal',
        postalCode: '8000-001',
      },
      settings: {
        timezone: 'Europe/Lisbon',
        alertsEnabled: false,
        positionUpdateInterval: 1500,
        maxAssets: 200,
      },
      isActive: false,
      createdAt: new Date('2024-01-20T11:00:00Z'),
      updatedAt: new Date('2024-02-01T14:00:00Z'),
    },
  ];
}

// Mutable state for mock data (reset between tests)
let mockFactories: Factory[] = createInitialFactories();

/**
 * Reset mock factories to initial state
 * Call this in beforeEach() to ensure test isolation
 */
export function resetFactoryMocks(): void {
  mockFactories = createInitialFactories();
}

export function mockListFactories(filters: FactoryFilter): Promise<FactoryListResponse> {
  return new Promise(resolve => {
    setTimeout(() => {
      let filtered = [...mockFactories];

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filtered = filtered.filter(
          factory =>
            factory.name.toLowerCase().includes(searchLower) ||
            factory.location.city.toLowerCase().includes(searchLower)
        );
      }

      if (filters.country) {
        filtered = filtered.filter(factory => factory.location.country === filters.country);
      }

      if (filters.isActive !== undefined) {
        filtered = filtered.filter(factory => factory.isActive === filters.isActive);
      }

      const page = filters.page || 1;
      const limit = filters.limit || 20;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginated = filtered.slice(startIndex, endIndex);

      resolve({
        factories: paginated,
        total: filtered.length,
        page,
        limit,
        hasMore: endIndex < filtered.length,
      });
    }, 300);
  });
}

export function mockGetFactory(factoryId: string): Promise<Factory> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const factory = mockFactories.find(f => f.id === factoryId);
      if (factory) {
        resolve(factory);
      } else {
        reject(new Error('Factory not found'));
      }
    }, 200);
  });
}

export function mockCreateFactory(data: FactoryCreateForm): Promise<Factory> {
  return new Promise(resolve => {
    setTimeout(() => {
      const newFactory: Factory = {
        id: `factory-${Date.now()}`,
        name: data.name,
        tenantId: 'tenant-123',
        location: data.location,
        settings: data.settings || {
          timezone: 'UTC',
          alertsEnabled: true,
          positionUpdateInterval: 1000,
          maxAssets: 1000,
        },
        floorPlan: data.floorPlan,
        isActive: data.isActive ?? true,
        metadata: {},
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      mockFactories.push(newFactory);
      resolve(newFactory);
    }, 400);
  });
}

export function mockUpdateFactory(factoryId: string, data: FactoryUpdateForm): Promise<Factory> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const factoryIndex = mockFactories.findIndex(f => f.id === factoryId);
      if (factoryIndex === -1) {
        reject(new Error('Factory not found'));
        return;
      }

      const updatedFactory: Factory = {
        ...mockFactories[factoryIndex],
        ...data,
        updatedAt: new Date(),
      };

      mockFactories[factoryIndex] = updatedFactory;
      resolve(updatedFactory);
    }, 300);
  });
}

export function mockDeleteFactory(
  factoryId: string
): Promise<{ success: boolean; message: string }> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const factoryIndex = mockFactories.findIndex(f => f.id === factoryId);
      if (factoryIndex === -1) {
        reject(new Error('Factory not found'));
        return;
      }

      mockFactories[factoryIndex].isActive = false;
      mockFactories[factoryIndex].updatedAt = new Date();

      resolve({
        success: true,
        message: 'Factory deleted successfully',
      });
    }, 250);
  });
}
